import { z } from 'zod';

const trimStringValue = (value: unknown): unknown => {
	if (typeof value !== 'string') {
		return value;
	}

	return value.trim();
};

const trimOptionalStringValue = (value: unknown): unknown => {
	if (typeof value !== 'string') {
		return value;
	}

	const trimmed = value.trim();

	return trimmed.length > 0 ? trimmed : undefined;
};

const parseNumberFromString = (
	value: unknown,
	pattern: RegExp,
	transform: (value: string) => number,
): unknown => {
	if (typeof value === 'number') {
		return value;
	}

	if (typeof value !== 'string') {
		return value;
	}

	const trimmed = value.trim();

	if (!pattern.test(trimmed)) {
		return Number.NaN;
	}

	return transform(trimmed);
};

const parseOptionalNumberFromString = (
	value: unknown,
	pattern: RegExp,
	transform: (value: string) => number,
): unknown => {
	if (value === undefined) {
		return undefined;
	}

	if (typeof value === 'string' && value.trim().length === 0) {
		return undefined;
	}

	return parseNumberFromString(value, pattern, transform);
};

const DECIMAL_STRING_PATTERN = /^\d+(?:\.\d+)?$/;
const INTEGER_STRING_PATTERN = /^\d+$/;

export const trimmedString = z.preprocess(trimStringValue, z.string().min(1));

export const optionalTrimmedString = z.preprocess(trimOptionalStringValue, z.string().optional());

export const stringToNumber = z.preprocess(
	(value) => parseNumberFromString(value, INTEGER_STRING_PATTERN, Number),
	z.number().refine(Number.isFinite, 'Expected finite number'),
);

export const optionalStringToNumber = z.preprocess(
	(value) => parseOptionalNumberFromString(value, INTEGER_STRING_PATTERN, Number),
	z.number().refine(Number.isFinite, 'Expected finite number').optional(),
);

export const stringToDecimalNumber = z.preprocess(
	(value) => parseNumberFromString(value, DECIMAL_STRING_PATTERN, Number),
	z.number().refine(Number.isFinite, 'Expected finite number'),
);

export const optionalStringToDecimalNumber = z.preprocess(
	(value) => parseOptionalNumberFromString(value, DECIMAL_STRING_PATTERN, Number),
	z.number().refine(Number.isFinite, 'Expected finite number').optional(),
);

export const stringToBool = z.union([z.string(), z.undefined()]).transform((v) => {
	if (v === '1') {
		return true;
	} else if (v === '0') {
		return false;
	} else {
		return false;
	}
});

export const stringToDate = z.preprocess((arg) => {
	if (typeof arg == 'string' || arg instanceof Date) return new Date(arg);
}, z.date());

export const stringToTime = z.custom<string>((val) =>
	/^(?:0?[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/.test(String(val)),
);
